'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Formik, useFormikContext, useField } from 'formik'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import Box, { BoxProps } from '@components/kit/box'
import Card from '@components/kit/card'
import { TextInput, TextInputProps } from '@components/kit/form/inputs'
import Text from '@components/kit/text'
import Button from '@components/kit/button'
import { CloseIcon } from '@components/kit/icons'
import Loading from '@components/kit/loading'
import Pressable from '@components/kit/pressable'

// lib
import { ChatContext } from '../chat.web'
import { useNotesStore } from '@store/Notes'
import { NotePublication, NotePublicationOwnerType } from '@store/Notes/types'
import Modal from '@components/kit/modal'
import Tabs, { Tab } from '@components/kit/tabs'
import { FormButtonPanel, FormDivider } from '@components/formFields'
import { Markdown } from '@components/markdown'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

const TEXT_INPUT_MAX_HEIGHT = 280 // goal is to allow approximately 16 lines

/**
 * Defines the available tabs for filtering notes by origin.
 * When NotePublicationOwnerType gains new values, add a corresponding entry here.
 */
type NotesTab = NotePublicationOwnerType | 'Notes'

const NOTES_TABS: { key: NotesTab; label: string }[] = [
  { key: 'Notes', label: 'Notes' },
  { key: NotePublicationOwnerType.MEMBER, label: 'Member' }
]

const filterPublicationsByTab = (
  publications: NotePublication[],
  tab: NotesTab
): NotePublication[] => {
  if (tab === NotePublicationOwnerType.MEMBER) {
    return publications.filter(
      pub => pub.change_owner_type === NotePublicationOwnerType.MEMBER
    )
  }
  return publications.filter(
    pub => pub.change_owner_type !== NotePublicationOwnerType.MEMBER
  )
}

/* -----------------------------------------------------------------------------
 * NoteTextInputField
 * -------------------------------------------------------------------------- */

type NoteTextInputFieldProps = TextInputProps & {
  name: string
  disabled?: boolean
}

const NoteTextInputField = React.forwardRef(
  (
    { name, disabled, ...props }: NoteTextInputFieldProps,
    ref: React.MutableRefObject<HTMLInputElement>
  ) => {
    const { handleSubmit } = useFormikContext()
    const [field] = useField(name)

    Object.assign(field, {
      onChangeText: field.onChange(name),
      onBlur: field.onBlur(name)
    })

    delete field['onChange']

    return (
      <Card
        flexDirection='column'
        minHeight='72px'
        p='16'
        backgroundColor={disabled ? 'shade02' : 'white'}
      >
        <TextInput
          multiline
          ref={ref}
          maxHeight={TEXT_INPUT_MAX_HEIGHT}
          // fontSize={14}
          font='text.12.medium'
          textColor='shade06'
          placeholder='Type note...'
          placeholderTextColor={'shade05'}
          onChange={handleSubmit}
          editable={!disabled}
          {...field}
          {...props}
        />
      </Card>
    )
  }
)

/* -----------------------------------------------------------------------------
 * PublicationItemText
 * -------------------------------------------------------------------------- */

type PublicationItemTextProps = {
  publication: NotePublication
  textColor?: typeof Text.defaultProps.textColor
  textFont?: typeof Text.defaultProps.font
}

const PublicationItemText = ({
  publication,
  textColor = 'walrusBlue200',
  textFont = 'display.14.bold'
}: PublicationItemTextProps) => {
  if (publication.markdown) {
    return <Markdown textColor={textColor}>{publication.text}</Markdown>
  }
  return (
    <Text font={textFont} textColor={textColor}>
      {publication.text}
    </Text>
  )
}

/* -----------------------------------------------------------------------------
 * PublicationItem
 * -------------------------------------------------------------------------- */

type PublicationItemProps = {
  publication: NotePublication
  refreshPublications: () => void
}

const PublicationItem = ({
  publication,
  refreshPublications
}: PublicationItemProps) => {
  const { userId } = useContext(ChatContext)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const { deleteUserNote } = useNotesStore.getState()

  const publicationOrigin = publication.change_owner_type
    .toString()
    .replaceAll('_', ' ')
    .toLocaleLowerCase()

  const formatTimestamp = (date: Date) => {
    return date.toLocaleString()
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    setDeleteError(null)
    try {
      const success = await deleteUserNote(userId, publication.id)
      if (success) {
        setIsDeleteModalOpen(false)
        refreshPublications()
      } else {
        setDeleteError('Failed to delete note. Please try again.')
      }
    } catch {
      setDeleteError('An unexpected error occurred. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCloseDeleteModal = () => {
    if (isDeleting) return
    setIsDeleteModalOpen(false)
    setDeleteError(null)
  }

  return (
    <Card paddingY='16' paddingLeft='16' paddingRight='24' marginX='12'>
      <Pressable
        position='absolute'
        top={6}
        right={6}
        padding='04'
        onPress={() => setIsDeleteModalOpen(true)}
      >
        <CloseIcon width={9} height={9} fill='walrusBlue500' />
      </Pressable>
      <Box marginBottom='08'>
        <PublicationItemText publication={publication} />
      </Box>

      <Box flex='1 1 auto' flexDirection='row' justifyContent='space-between'>
        <Text variant='meta' textColor='walrusNeutral700'>
          {formatTimestamp(publication._created)}
        </Text>
        <Text
          variant='meta'
          textColor='walrusNeutral700'
          textTransform='capitalize'
        >
          {`Origin: ${publicationOrigin}`}
        </Text>
      </Box>
      <Modal open={isDeleteModalOpen} showCloseButton={false} maxWidth='30vw'>
        <Text font='display.16.bold' textColor='walrusBlue200'>
          You're trying to delete the following note:
        </Text>
        <Card marginY='16' padding='16' backgroundColor='walrusRed050'>
          <Box marginBottom='08' overflowY='auto' maxHeight='50vh'>
            <PublicationItemText
              publication={publication}
              textColor='walrusRed800'
            />
          </Box>

          <Box
            flex='1 1 auto'
            flexDirection='row'
            justifyContent='space-between'
          >
            <Text variant='meta' textColor='walrusNeutral700'>
              {formatTimestamp(publication._created)}
            </Text>
            <Text
              variant='meta'
              textColor='walrusNeutral700'
              textTransform='capitalize'
            >
              {`Origin: ${publicationOrigin}`}
            </Text>
          </Box>
        </Card>
        <Text font='display.16.bold' textColor='walrusBlue200'>
          Are you sure you want to delete this note?
        </Text>
        {deleteError && (
          <Text font='text.12.medium' textColor='walrusRed800' mt='08'>
            {deleteError}
          </Text>
        )}
        {isDeleting && <Loading />}
        <Box flexDirection='row' justifyContent='flex-end'>
          <FormButtonPanel
            onCancel={handleCloseDeleteModal}
            onSave={handleDelete}
            saveLabel='Delete'
            cancelLabel='Cancel'
            saveVariant='paleRed'
            disabled={isDeleting}
          />
        </Box>
      </Modal>
    </Card>
  )
}

/* -----------------------------------------------------------------------------
 * Notes
 * -------------------------------------------------------------------------- */

// the new-note form
type NoteFormValues = { user_id: number; text: string }

export type NotesProps = BoxProps & {}

function Notes(props: NotesProps) {
  const { userId } = React.useContext(ChatContext)
  const initialValues: Partial<NoteFormValues> = { user_id: userId }
  const [publications, setPublications] = useState<NotePublication[]>([])
  const [shouldFetch, setShouldFetch] = useState(true)
  const [isSubmittingNote, setIsSubmittingNote] = useState(false)
  const [activeTab, setActiveTab] = useState<NotesTab>('Notes')
  const listRef = useRef<HTMLDivElement>(null)
  const [listHeight, setListHeight] = useState<number | 'auto'>('auto')

  const filteredPublications = filterPublicationsByTab(publications, activeTab)
  const { getUserNotes, postUserNote } = useNotesStore.getState()

  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current
    // Temporarily set auto to measure the natural height
    el.style.height = 'auto'
    const naturalHeight = el.scrollHeight
    // Restore previous height so the transition starts from it
    el.style.height = `${listHeight === 'auto' ? naturalHeight : listHeight}px`
    // Force reflow, then set the target height
    void el.offsetHeight
    setListHeight(naturalHeight)
  }, [filteredPublications.length, activeTab])

  const handlePostNewNote = async (
    values: Partial<NoteFormValues>,
    onSuccessCallback: () => void
  ) => {
    if (!values?.text?.trim()) return

    setIsSubmittingNote(true)
    const result = await postUserNote(userId, values.text.trim())

    if (result.success && result.note) {
      setPublications(prev => [result.note, ...prev])
      onSuccessCallback()
      setShouldFetch(true)
    }
    // the timer this sat in never delayed it: the call ran right away
    setIsSubmittingNote(false)
  }

  useEffect(() => {
    const fetchNotesData = async () => {
      const notes = await getUserNotes(userId)
      setPublications(notes)
      setShouldFetch(false)
    }
    if (shouldFetch) {
      fetchNotesData()
    }
  }, [userId, getUserNotes, shouldFetch])

  return (
    <Box {...accessibilityLabel('MemberNotes')} {...props}>
      <Box mt='24' mb='12' flexDirection='row' alignItems='baseline'>
        <Text ml='12' font='display.16.bold'>
          Notes
        </Text>
      </Box>
      <Formik initialValues={initialValues} key={userId} onSubmit={() => {}}>
        {({ values, resetForm }) => (
          <Box flexDirection='column' alignContent='flex-end' gap='12'>
            <NoteTextInputField
              flex='1 1 auto'
              name='text'
              disabled={isSubmittingNote}
            />
            <Box
              flex='1 1 auto'
              flexDirection='row'
              alignItems='flex-end'
              justifyContent='flex-end'
            >
              <Button
                ml='12'
                variant='secondary'
                size='small'
                onPress={() => {
                  handlePostNewNote(values, () =>
                    resetForm({ values: { user_id: userId, text: '' } })
                  )
                }}
                disabled={!values.text?.trim() || isSubmittingNote}
              >
                Submit
              </Button>
            </Box>
          </Box>
        )}
      </Formik>

      {(shouldFetch || isSubmittingNote) && <Loading />}

      {!!publications.length && (
        <Box mt='12' mb='08'>
          <Box
            flex='1'
            height='2px'
            bg='shade04b'
            position='absolute'
            bottom='0'
            left='0'
            right='0'
          />
          <Tabs>
            {NOTES_TABS.map(tab => (
              <Tab
                key={tab.key}
                bg='transparent'
                onPress={() => setActiveTab(tab.key)}
                active={activeTab === tab.key}
              >
                {tab.label}
              </Tab>
            ))}
          </Tabs>
        </Box>
      )}

      {!!filteredPublications.length && (
        <FormDivider dividerColor='transparent'>
          {filteredPublications.length} Note
          {filteredPublications.length > 1 && 's'}
        </FormDivider>
      )}

      {!filteredPublications.length && (
        <FormDivider dividerColor='transparent'>
          No{' '}
          {activeTab === 'Notes' ? "clinical team's notes" : "member's notes"}{' '}
          to display.
        </FormDivider>
      )}

      <div
        ref={listRef}
        style={{
          marginTop: 8,
          height: typeof listHeight === 'number' ? `${listHeight}px` : 'auto',
          transition: 'height 0.3s ease',
          overflow: 'hidden'
        }}
      >
        <Box gap='12'>
          {filteredPublications.map(publication => (
            <PublicationItem
              key={publication.id}
              publication={publication}
              refreshPublications={() => setShouldFetch(true)}
            />
          ))}
        </Box>
      </div>
    </Box>
  )
}

export default Notes
